/**
 * Phone-number matching for admin search boxes.
 *
 * Phones are free text (profiles.phone, bookings.phone), so one number shows
 * up as "+49 151 2345678", "0049 151 2345678", "0151 2345678" or
 * "+49 (0)151 2345678". Matching compares digits only and, when a country
 * code is known, the national part without that code or the trunk "0" —
 * so any of those forms finds the others, and partial numbers still match.
 *
 * COL-488.
 */

// E.164 calling codes are prefix-free: "1" and "7" are the only one-digit
// codes, these are the two-digit ones, and every other code has three digits.
const TWO_DIGIT_CALLING_CODES = new Set([
  "20",
  "27",
  "30",
  "31",
  "32",
  "33",
  "34",
  "36",
  "39",
  "40",
  "41",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "49",
  "51",
  "52",
  "53",
  "54",
  "55",
  "56",
  "57",
  "58",
  "60",
  "61",
  "62",
  "63",
  "64",
  "65",
  "66",
  "81",
  "82",
  "84",
  "86",
  "90",
  "91",
  "92",
  "93",
  "94",
  "95",
  "98",
]);

// A stored number written nationally ("0151 …") is taken as German — the
// same default as normalisePhone's DEFAULT_COUNTRY_CODE in
// supabase/functions/_shared/whatsapp.ts.
const DEFAULT_COUNTRY_CODE = "49";

// Invisible direction/format marks that WhatsApp and Contacts wrap around
// copied numbers (U+202A … U+202C, U+2066 … U+2069, …).
const FORMAT_CHARS = /\p{Cf}/gu;

// Only digits, common separators (incl. en/em dashes) and an optional "+",
// which may follow an opening bracket: "(+49) 151". Anything with letters
// ("Room 12") is a text search, not a phone search.
const PHONE_QUERY_PATTERN = /^[\s(]*\+?[\d\s()./\-–—]+$/;
const LEADING_PLUS = /^[\s(]*\+/;

// Below this many national digits a query is still being typed, so it is
// matched as a prefix rather than anywhere in the number ("+49 1" should not
// match every German number that contains a 1).
const MIN_NATIONAL_DIGITS_FOR_INFIX = 3;

export type ParsedPhone = {
  /** Every digit, without a leading international "00". */
  digits: string;
  /**
   * Calling code when written internationally ("+" or "00"), or the German
   * default for a stored national number; null when unknown.
   */
  countryCode: string | null;
  /** The number without calling code and without trunk "0"s. */
  national: string;
};

function callingCodeLength(digits: string): number {
  if (digits.startsWith("1") || digits.startsWith("7")) return 1;
  if (TWO_DIGIT_CALLING_CODES.has(digits.slice(0, 2))) return 2;
  return 3;
}

function parse(raw: string, assumeDefaultCountry: boolean): ParsedPhone {
  const cleaned = raw.replace(FORMAT_CHARS, "").trim();
  let digits = cleaned.replace(/\D/g, "");
  const hasPlus = LEADING_PLUS.test(cleaned);
  if (hasPlus || digits.startsWith("00")) {
    if (!hasPlus) digits = digits.slice(2);
    const countryCode = digits.slice(0, callingCodeLength(digits));
    return {
      digits,
      countryCode: countryCode || null,
      // Also drops the "(0)" in "+49 (0)151 …".
      national: digits.slice(countryCode.length).replace(/^0+/, ""),
    };
  }
  const national = digits.replace(/^0+/, "");
  return {
    digits,
    countryCode:
      assumeDefaultCountry && digits.startsWith("0") && national
        ? DEFAULT_COUNTRY_CODE
        : null,
    national,
  };
}

/** Parse a stored phone number. */
export function parsePhone(raw: string): ParsedPhone {
  return parse(raw, true);
}

/**
 * Parse a search box value as a phone query, or null when it is not one
 * (letters, or too few digits to be meaningful). Parse once per search, then
 * test each phone with {@link phoneMatchesQuery}.
 *
 * Unlike a stored number, a query without "+"/"00" stays country-agnostic,
 * so "0151 234" still finds a number whatever country it was saved under.
 */
export function parsePhoneQuery(query: string): ParsedPhone | null {
  const cleaned = query.replace(FORMAT_CHARS, "");
  if (!PHONE_QUERY_PATTERN.test(cleaned.trim())) return null;
  const parsed = parse(cleaned, false);
  // "+1" / "+49" work on their own; "015…" needs 3 digits; a bare digit run
  // needs 4, so room numbers and postcodes ("101") stay title searches.
  const minDigits = parsed.countryCode
    ? Math.min(2, callingCodeLength(parsed.digits))
    : parsed.digits.startsWith("0")
      ? 3
      : 4;
  return parsed.digits.length >= minDigits ? parsed : null;
}

function nationalMatches(phoneNational: string, queryNational: string) {
  return queryNational.length >= MIN_NATIONAL_DIGITS_FOR_INFIX
    ? phoneNational.includes(queryNational)
    : phoneNational.startsWith(queryNational);
}

export function phoneMatchesQuery(
  phone: string | null | undefined,
  query: ParsedPhone | null
): boolean {
  if (!phone || !query) return false;
  const p = parsePhone(phone);
  if (!p.digits) return false;

  if (!query.countryCode) {
    // "2345678", "4915…" typed without a plus, or "0151 …" / "015" typed
    // nationally, matched on the national part.
    return (
      p.digits.includes(query.digits) ||
      (query.digits.startsWith("0") &&
        nationalMatches(p.national, query.national))
    );
  }

  // Only a (possibly partial) calling code so far: "+49", "+35", "+49 (0)".
  if (!query.national) {
    const canonical = p.countryCode ? p.countryCode + p.national : p.digits;
    return canonical.startsWith(query.countryCode);
  }

  // Both have a country: a different one is never a match.
  if (p.countryCode) {
    return (
      p.countryCode === query.countryCode &&
      nationalMatches(p.national, query.national)
    );
  }

  // Stored as bare digits with no country ("49151…" or "151…"): it may or
  // may not start with a calling code, so only match from the start.
  // Compare without the query's trunk "0" so "+49 (0)151" finds "49151…".
  return (
    p.digits.startsWith(query.countryCode + query.national) ||
    p.national.startsWith(query.national)
  );
}
